/**
 * Кому отдать фокус, когда окно открылось.
 *
 * Функция - когда окно само знает нужный узел (поле ввода, кнопка "Отмена"). Строка `'body'` -
 * когда фокусировать нечего: первым интерактивным элементом оказывается разрушительное действие
 * ("Завершить сессию", "Удалить аккаунт"), и подсвечивать его при открытии нельзя.
 *
 * `null` в этот тип не входит намеренно - см. `resolveDialogInitialFocus`.
 */
export type DialogInitialFocus = 'body' | (() => HTMLElement | undefined);

/**
 * Приводит просьбу окна к виду, на котором focus-trap не падает.
 *
 * Ради чего это существует: `initialFocusEl` уходит в `@zag-js/focus-trap`, и его
 * `getNodeForOption` принимает узел, `undefined` и `false`, а на `null` бросает
 * "`initialFocus` was specified but was not a node, or did not return a node". Тип zag
 * (`MaybeElement = Nullable<HTMLElement>`) `null` при этом разрешает, так что компилятор от мины
 * не спасал: `initialFocusEl={() => null}` писали как "не фокусируй ничего", и это копировалось
 * из окна в окно.
 *
 * Чем оно кончалось: при ОТКРЫТИИ исключение глотает `try/catch` внутри zag, а при ЗАКРЫТИИ окна,
 * открытого поверх этого (жалоба на сообщение, подтверждение выхода), снятие паузы с родительского
 * трапа идёт из React-cleanup и долетает до `errorComponent` роута - человек получает
 * "We couldn't load this page" вместо страницы. На проде это живая ошибка: три issue в GlitchTip,
 * 25 событий с 01.09.2026, уровень error.
 *
 * Поэтому здесь два слоя. Тип не пускает `null` на входе, а рантайм превращает в `undefined`
 * любой `null`, который всё же вернулся - в том числе из собственной функции окна, где
 * `() => ref.current` даёт `null` у ещё не смонтированного узла.
 *
 * @param getElementById подменяется в тестах; в приложении это `document.getElementById`.
 */
export const resolveDialogInitialFocus = (
  initialFocusEl: DialogInitialFocus | undefined,
  contentId: string,
  getElementById: (id: string) => HTMLElement | null = (id) => document.getElementById(id)
): (() => HTMLElement | undefined) | undefined => {
  if (initialFocusEl === undefined) {
    return undefined;
  }

  if (initialFocusEl === 'body') {
    /*
      Узел тела окна рисует `DialogContent`, и ref наружу он не отдаёт. Зато zag ставит на тело
      ровно тот id, который ему передали через `ids`, - поэтому имя даём мы, а по имени и находим
      настоящий узел. У тела `tabindex="-1"`, так что фокус на нём ничего не подсвечивает.
    */
    return () => getElementById(contentId) ?? undefined;
  }

  return () => initialFocusEl() ?? undefined;
};
